import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { createUniqueFilename } from '../db/adapter/desktopPathUtils';
import { playBackupSound } from '@/utils/notifications/sound/notificationSoundHelper';
import { StudentData } from '@/contexts/student/types';
import { unifiedStudentStorage } from '../storage/unifiedStudentStorage';

/**
 * Creates a backup of all student data
 * @returns Promise resolving to the backup ID
 */
export const createStudentBackup = async (): Promise<string> => {
  try {
    console.log('🔄 Starting student backup creation...');
    
    // Get students directly from unified storage instead of context
    await unifiedStudentStorage.initialize();
    const students = await unifiedStudentStorage.getAllStudents();
    
    if (!students || students.length === 0) {
      console.log('⚠️ No students found for backup');
      toast.warning('No student data to backup');
      throw new Error('No student data to backup');
    }
    
    console.log(`📦 Found ${students.length} students for backup`);
    
    // Process all image and document URLs to ensure they're backed up properly
    const processedStudents = await Promise.all(students.map(async (student) => {
      const processedStudent = { ...student };
      
      // Process profile image if it exists
      if (student.profileImage && student.profileImage.startsWith('data:')) {
        // It's already an embedded data URL, no need to process
        console.log(`✅ Profile image already embedded for student: ${student.name}`);
      } else if (student.profileImage) {
        try {
          // For file URLs, try to convert to data URL
          const response = await fetch(student.profileImage);
          const blob = await response.blob();
          const reader = new FileReader();
          const dataUrl = await new Promise<string>((resolve) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          processedStudent.profileImage = dataUrl;
          console.log(`✅ Converted profile image to data URL for student: ${student.name}`);
        } catch (error) {
          console.warn(`Could not process student profile image: ${error}`);
        }
      }
      
      // Process document URLs if they exist
      if (student.documents && student.documents.length > 0) {
        processedStudent.documents = await Promise.all(
          student.documents.map(async (doc) => {
            const processedDoc = { ...doc };
            if (doc.url && !doc.url.startsWith('data:')) {
              try {
                // Convert document URL to data URL if possible
                const response = await fetch(doc.url);
                const blob = await response.blob();
                const reader = new FileReader();
                const dataUrl = await new Promise<string>((resolve) => {
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.readAsDataURL(blob);
                });
                processedDoc.url = dataUrl;
                console.log(`✅ Converted document to data URL: ${doc.name}`);
              } catch (error) {
                console.warn(`Could not process student document: ${error}`);
              }
            }
            return processedDoc;
          })
        );
      }
      
      return processedStudent;
    }));
    
    // Create backup object with timestamp and metadata
    const backupId = uuidv4();
    const backup = {
      id: backupId,
      version: '1.0',
      timestamp: new Date().toISOString(),
      data: {
        students: processedStudents,
        metadata: {
          backupCreatedAt: new Date().toISOString(),
          backupType: 'student',
          studentCount: students.length,
          systemVersion: '1.0.0',
          includesImages: true,
          includesDocuments: true
        }
      }
    };

    // Save backup to registry for later restoration
    saveStudentBackupToRegistry(backup);

    const backupStr = JSON.stringify(backup);
    const filename = `nalc-student-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    console.log(`💾 Creating backup file: ${filename}`);
    
    // Save to file system if on native platform
    if (Capacitor.isNativePlatform()) {
      try {
        // Save backup file to app documents directory
        await Filesystem.writeFile({
          path: `nalc_tracker/backups/${createUniqueFilename(filename)}`,
          data: backupStr,
          directory: Directory.Documents,
          recursive: true
        });
        toast.success('Student backup saved to documents folder');
        await playBackupSound();
      } catch (error) {
        console.error('Error saving student backup to filesystem:', error);
        // Fallback to browser download if native save fails
        const blob = new Blob([backupStr], { type: 'application/json' });
        saveAs(blob, filename);
      }
    } else {
      // Browser environment - use FileSaver
      const blob = new Blob([backupStr], { type: 'application/json' });
      saveAs(blob, filename);
      await playBackupSound();
    }
    
    // Update last backup timestamp in localStorage
    localStorage.setItem('lastStudentBackupDate', new Date().toISOString());
    
    console.log(`✅ Student backup created successfully with ${students.length} students`);
    toast.success(`Student backup created successfully (${students.length} students)`);
    
    return backupId;
  } catch (error) {
    console.error('❌ Student backup creation failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to create student backup: ${errorMessage}`);
    throw error;
  }
};

/**
 * Restore student data from a backup file
 * @param backupFile The backup file to restore from
 */
export const restoreStudentBackup = async (backupFile: File): Promise<boolean> => {
  try {
    console.log('🔄 Starting student backup restoration...');
    
    // Read file
    const fileContent = await readFileAsText(backupFile);
    
    // Parse backup data
    const backup = JSON.parse(fileContent);
    
    // Validate backup format 
    const students = backup.data?.students || backup.students;
    if (!students || !Array.isArray(students)) {
      console.error('❌ Invalid backup file format: No student data found');
      toast.error('Invalid backup file format: No student data found');
      return false;
    }
    
    console.log(`📦 Found ${students.length} students in backup file`);
    
    // Initialize unified storage
    await unifiedStudentStorage.initialize();
    
    // Clear existing students first
    const existingStudents = await unifiedStudentStorage.getAllStudents();
    for (const student of existingStudents) {
      await unifiedStudentStorage.deleteStudent(student.id);
    }
    
    console.log('🗑️ Cleared existing student data');
    
    // Restore students one by one
    let successCount = 0;
    let errorCount = 0;
    
    for (const studentData of students) {
      try {
        await unifiedStudentStorage.addStudent(studentData);
        successCount++;
      } catch (error) {
        console.error(`Failed to restore student ${studentData.name}:`, error);
        errorCount++;
      }
    }
    
    console.log(`✅ Restoration completed: ${successCount} successful, ${errorCount} failed`);
    
    // Update last backup timestamp in localStorage
    localStorage.setItem('lastStudentBackupDate', new Date().toISOString());
    
    if (errorCount > 0) {
      toast.warning(`Student restoration completed with ${errorCount} errors. ${successCount} students restored successfully.`);
    } else {
      toast.success(`Successfully restored ${successCount} students from backup`);
    }
    
    await playBackupSound();
    return true;
  } catch (error) {
    console.error('❌ Student restoration failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to restore student backup: ${errorMessage}`);
    return false;
  }
};

/**
 * List all available local student backups
 */
export const listStudentBackups = (): {id: string, timestamp: string, size: number, description?: string}[] => {
  try {
    const backupRegistry = localStorage.getItem('student_backup_registry');
    return backupRegistry ? JSON.parse(backupRegistry) : [];
  } catch (error) {
    console.error('Error listing student backups:', error);
    return [];
  }
};

/**
 * Restore from a local backup ID
 */
export const restoreFromLocalStudentBackup = async (backupId: string): Promise<boolean> => {
  try {
    console.log(`🔄 Restoring from local backup: ${backupId}`);
    
    const backup = getStudentBackup(backupId);
    
    if (!backup || (!backup.data?.students && !backup.students)) {
      console.error('❌ Invalid student backup data');
      toast.error('Invalid student backup data');
      return false;
    }
    
    // Ensure we get the correct students array
    const students = backup.data?.students || backup.students || [];
    
    console.log(`📦 Found ${students.length} students in local backup`);
    
    // Initialize unified storage
    await unifiedStudentStorage.initialize();
    
    // Clear existing students first
    const existingStudents = await unifiedStudentStorage.getAllStudents();
    for (const student of existingStudents) {
      await unifiedStudentStorage.deleteStudent(student.id);
    }
    
    console.log('🗑️ Cleared existing student data');
    
    // Restore students one by one
    let successCount = 0;
    let errorCount = 0;
    
    for (const studentData of students) {
      try {
        await unifiedStudentStorage.addStudent(studentData);
        successCount++;
      } catch (error) {
        console.error(`Failed to restore student ${studentData.name}:`, error);
        errorCount++;
      }
    }
    
    console.log(`✅ Local restoration completed: ${successCount} successful, ${errorCount} failed`);
    
    if (errorCount > 0) {
      toast.warning(`Local student backup restored with ${errorCount} errors. ${successCount} students restored successfully.`);
    } else {
      toast.success(`Local student backup restored successfully (${successCount} students)`);
    }
    
    return true;
  } catch (error) {
    console.error('❌ Local student restoration failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to restore from local student backup: ${errorMessage}`);
    return false;
  }
};

/**
 * Save backup to registry for later restoration
 */
const saveStudentBackupToRegistry = (backup: any): void => {
  try {
    const registry = localStorage.getItem('student_backup_registry');
    const backups = registry ? JSON.parse(registry) : [];
    
    // Add new backup to registry
    backups.push({
      id: backup.id,
      timestamp: backup.timestamp,
      size: JSON.stringify(backup).length,
      description: backup.data?.metadata?.description || 'Student backup'
    });
    
    // Keep only last 10 backups to prevent localStorage bloat
    const recentBackups = backups.slice(-10);
    
    // Save registry
    localStorage.setItem('student_backup_registry', JSON.stringify(recentBackups));
    
    // Save backup data
    localStorage.setItem(`student_backup_${backup.id}`, JSON.stringify(backup));
    
    console.log('✅ Backup saved to registry');
  } catch (error) {
    console.error('Error saving student backup to registry:', error);
  }
};

/**
 * Get backup data by ID
 */
const getStudentBackup = (id: string): any => {
  try {
    const backup = localStorage.getItem(`student_backup_${id}`);
    return backup ? JSON.parse(backup) : null;
  } catch (error) {
    console.error('Error retrieving student backup:', error);
    return null;
  }
};

/**
 * Read a file as text
 * @param file The file to read
 */
const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};
